import * as React from "react";
import {
  Controller,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import PhoneNumberField from "./PhoneNumberField";
import TextField from "./TextField";
import { twMerge } from "tailwind-merge";

export type FormFieldType = "phone" | "text";

export type FormDropdownOption = {
  value: string;
  label: string;
};

export type FormField = {
  name: string;
  label?: string;
  /** Use `face` going forward (keeps `type` for backwards compatibility). */
  face?: FormFieldType;
  rules?: RegisterOptions;
  placeholder?: string;
  options?: ReadonlyArray<FormDropdownOption>;
} & Record<string, any>;

export type FormProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  /**
   * Layout supports:
   * - `[field, field]` meaning two separate rows
   * - `[[field, field], field, [field]]` meaning mixed rows
   *
   * Any array item renders as a single row (columns).
   */
  fields: (FormField | FormField[])[];
  className?: string;
};

function getIn(obj: any, path: string): any {
  if (!obj) return undefined;
  const normalized = path.replace(/\[(\d+)\]/g, ".$1");
  const parts = normalized.split(".").filter(Boolean);
  let cur = obj;
  for (const key of parts) {
    if (cur == null) return undefined;
    cur = cur[key];
  }
  return cur;
}

type FormFieldItemProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  field: FormField;
};

function FormFieldItem<TFieldValues extends FieldValues = FieldValues>({
  form,
  field: { name, rules, face, ...others },
}: FormFieldItemProps<TFieldValues>) {
  return (
    <div className="flex-1">
      <Controller
        control={form.control}
        name={name as any}
        rules={rules as any}
        render={({ field, formState }) => {
          const error = getIn(formState.errors, name);
          const errorMessage =
            error?.message ||
            (error?.type === "required" && `${others.label} is required`);

          if (face === "phone") {
            const { ref: _ref, value, ...rest } = field;
            return (
              <PhoneNumberField
                {...others}
                {...rest}
                required={!!rules?.required}
                value={String(value ?? "")}
                errorMessage={errorMessage}
              />
            );
          }
          const { ref: _ref, value, ...rest } = field;
          return (
            <TextField
              required={!!rules?.required}
              value={String(value ?? "")}
              errorMessage={errorMessage}
              {...others}
              {...rest}
            />
          );
        }}
      />
    </div>
  );
}

export default function Form<TFieldValues extends FieldValues = FieldValues>({
  form,
  fields,
  className,
}: FormProps<TFieldValues>) {
  const rows = React.useMemo<FormField[][]>(
    () =>
      (fields ?? [])
        .map((row) => (Array.isArray(row) ? row : [row]))
        .filter(Boolean),
    [fields],
  );

  return (
    <div className={twMerge("flex flex-col gap-4", className)}>
      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className="flex flex-col gap-4 sm:flex-row sm:items-start"
        >
          {row.map((f) => (
            <FormFieldItem key={f.name} form={form} field={f} />
          ))}
        </div>
      ))}
    </div>
  );
}
